import {toDayKey} from '../TeamActivity/constants';

export type RangeUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type GraphBy = 'auto' | RangeUnit;

export interface DateRangeSelection {
  amount: number;
  unit: RangeUnit;
  graphBy: GraphBy;
}

export const DEFAULT_DATE_RANGE: DateRangeSelection = {amount: 1, unit: 'month', graphBy: 'auto'};

export const RANGE_UNIT_OPTIONS: {key: RangeUnit; label: string; pluralLabel: string}[] = [
  {key: 'day', label: 'Day', pluralLabel: 'Days'},
  {key: 'week', label: 'Week', pluralLabel: 'Weeks'},
  {key: 'month', label: 'Month', pluralLabel: 'Months'},
  {key: 'quarter', label: 'Quarter', pluralLabel: 'Quarters'},
  {key: 'year', label: 'Year', pluralLabel: 'Years'},
];

export const GRAPH_BY_OPTIONS: {key: GraphBy; label: string}[] = [
  {key: 'auto', label: 'Auto'},
  {key: 'day', label: 'Day'},
  {key: 'week', label: 'Week'},
  {key: 'month', label: 'Month'},
  {key: 'quarter', label: 'Quarter'},
  {key: 'year', label: 'Year'},
];

export const formatRangeLabel = (selection: DateRangeSelection) => {
  const opt = RANGE_UNIT_OPTIONS.find((o) => o.key === selection.unit)!;
  const unitLabel = selection.amount === 1 ? opt.label.toLowerCase() : opt.pluralLabel.toLowerCase();
  return `last ${selection.amount} ${unitLabel}`;
};

export interface CompletionRow {
  userId: string;
  userName: string;
  day: string; // YYYY-MM-DD (UTC, matches backend grouping)
  count: number;
  lastAt: string;
}

export interface PendingRow {
  status: string;
  count: number;
  oldest: string | null;
}

export interface VetterOption {
  id: string;
  name: string;
  email: string;
}

export interface VettingConfig {
  trackedUserIds: string[];
  caseFromStatuses: string[];
  caseToStatuses: string[];
  pendingStatuses: string[];
}

export interface VettingStats {
  config: VettingConfig;
  trackedUsers: {id: string; name: string; email: string}[];
  caseCompletions: CompletionRow[];
  providerCompletions: CompletionRow[];
  pending: PendingRow[];
}

export const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  entered: 'Entered',
  processing: 'Processing',
  awaiting_law_firm_info: 'Awaiting Law Firm Info',
  vetted: 'Vetted',
  closed_not_sent: 'Closed - Not Sent',
  supervisor_review: 'Supervisor Review',
  needs_further_review: 'Needs Further Review',
  hold_requests_from_ciox: 'Hold Requests from Ciox',
};

export const statusLabel = (status: string) => STATUS_LABELS[status] || status;

// Fixed-order categorical palette (CVD-validated); extra users fold into gray
export const USER_COLORS = ['#AE3EC9', '#4C6EF5', '#0CA678', '#F08C00', '#E64980'];
export const OTHER_COLOR = '#9A93A8';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAY_MS = 24 * 60 * 60 * 1000;

const utcMidnight = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

// Weeks run Monday–Sunday
const mondayOf = (d: Date) => {
  const m = utcMidnight(d);
  m.setUTCDate(m.getUTCDate() - ((m.getUTCDay() + 6) % 7));
  return m;
};

const parseDayKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

export const fmtBucketDay = (key: string) => {
  const [, m, d] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
};

// Subtract `amount` of `unit` from `date`. Month/quarter/year use calendar arithmetic
// (variable-length), day/week use fixed millisecond steps.
const subtractUnits = (date: Date, unit: RangeUnit, amount: number): Date => {
  if (unit === 'day') return new Date(date.getTime() - amount * DAY_MS);
  if (unit === 'week') return new Date(date.getTime() - amount * 7 * DAY_MS);
  const monthsPerUnit = unit === 'month' ? 1 : unit === 'quarter' ? 3 : 12;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - monthsPerUnit * amount, date.getUTCDate()));
};

const RANGE_LENGTH_DAYS: Record<RangeUnit, number> = {day: 1, week: 7, month: 30, quarter: 91, year: 365};

const GRAIN_ORDER: RangeUnit[] = ['day', 'week', 'month', 'quarter', 'year'];

const resolveGraphBy = (selection: DateRangeSelection): RangeUnit => {
  if (selection.graphBy !== 'auto') return selection.graphBy;
  const rangeDays = selection.amount * RANGE_LENGTH_DAYS[selection.unit];
  if (rangeDays <= 21) return 'day';
  if (rangeDays <= 112) return 'week';
  if (rangeDays <= 913) return 'month';
  if (rangeDays <= 1826) return 'quarter';
  return 'year';
};

interface BucketDef {
  start: Date;
  label: string;
}

const buildBuckets = (rangeStart: Date, end: Date, unit: RangeUnit): BucketDef[] => {
  const buckets: BucketDef[] = [];

  if (unit === 'day') {
    let cur = utcMidnight(rangeStart);
    const last = utcMidnight(end);
    while (cur <= last) {
      buckets.push({start: cur, label: fmtBucketDay(toDayKey(cur))});
      cur = new Date(cur.getTime() + DAY_MS);
    }
  } else if (unit === 'week') {
    let cur = mondayOf(rangeStart);
    const last = mondayOf(end);
    while (cur <= last) {
      buckets.push({start: cur, label: fmtBucketDay(toDayKey(cur))});
      cur = new Date(cur.getTime() + 7 * DAY_MS);
    }
  } else if (unit === 'month') {
    let cur = new Date(Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth(), 1));
    const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    while (cur <= last) {
      const label =
        buckets.length === 0 || cur.getUTCMonth() === 0
          ? `${MONTHS[cur.getUTCMonth()]} '${String(cur.getUTCFullYear()).slice(2)}`
          : MONTHS[cur.getUTCMonth()];
      buckets.push({start: cur, label});
      cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
    }
  } else if (unit === 'quarter') {
    const qStartMonth = (m: number) => Math.floor(m / 3) * 3;
    let cur = new Date(Date.UTC(rangeStart.getUTCFullYear(), qStartMonth(rangeStart.getUTCMonth()), 1));
    const last = new Date(Date.UTC(end.getUTCFullYear(), qStartMonth(end.getUTCMonth()), 1));
    while (cur <= last) {
      const q = Math.floor(cur.getUTCMonth() / 3) + 1;
      buckets.push({start: cur, label: `Q${q} '${String(cur.getUTCFullYear()).slice(2)}`});
      cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 3, 1));
    }
  } else {
    let cur = new Date(Date.UTC(rangeStart.getUTCFullYear(), 0, 1));
    const last = new Date(Date.UTC(end.getUTCFullYear(), 0, 1));
    while (cur <= last) {
      buckets.push({start: cur, label: `${cur.getUTCFullYear()}`});
      cur = new Date(Date.UTC(cur.getUTCFullYear() + 1, 0, 1));
    }
  }

  return buckets;
};

const indexerFor = (buckets: BucketDef[]) => (dayKey: string) => {
  if (!buckets.length) return -1;
  const d = parseDayKey(dayKey);
  if (d < buckets[0].start) return -1;
  for (let i = buckets.length - 1; i >= 0; i--) {
    if (d >= buckets[i].start) return i;
  }
  return -1;
};

export interface DateRangeWindow {
  startDate: string;
  endDate: string;
  prevStartDate: string;
  prevEndDate: string;
  bucketLabels: string[];
  bucketCount: number;
  bucketIndexOf: (dayKey: string) => number; // -1 when outside the window
}

// Max buckets before the graph-by grain is coerced coarser, so a huge manually-picked
// range (e.g. "Last 5 Years" graphed by Day) doesn't render thousands of bars.
const MAX_BUCKETS = 200;

export function computeDateRangeWindow(selection: DateRangeSelection, now = new Date()): DateRangeWindow {
  const end = now;
  const rangeStart = subtractUnits(end, selection.unit, selection.amount);

  let unit = resolveGraphBy(selection);
  let buckets = buildBuckets(rangeStart, end, unit);
  while (buckets.length > MAX_BUCKETS && GRAIN_ORDER.indexOf(unit) < GRAIN_ORDER.length - 1) {
    unit = GRAIN_ORDER[GRAIN_ORDER.indexOf(unit) + 1];
    buckets = buildBuckets(rangeStart, end, unit);
  }

  const prevStart = subtractUnits(rangeStart, selection.unit, selection.amount);
  const prevEnd = new Date(rangeStart.getTime() - 1);

  return {
    startDate: rangeStart.toISOString(),
    endDate: end.toISOString(),
    prevStartDate: prevStart.toISOString(),
    prevEndDate: prevEnd.toISOString(),
    bucketLabels: buckets.map((b) => b.label),
    bucketCount: buckets.length,
    bucketIndexOf: indexerFor(buckets),
  };
}

export interface UserSeries {
  userId: string;
  userName: string;
  tracked: boolean;
  color: string;
  caseTotal: number;
  casePerBucket: number[];
  providerTotal: number;
  providerPerBucket: number[];
  total: number;
  lastAt: string | null;
}

/**
 * One series per tracked user, always present (zero-filled), carrying both the case and
 * provider metrics. Completions by unselected users are excluded entirely; only when no
 * team is configured yet does everyone with completions appear, so the page is useful
 * pre-setup.
 */
export function buildUserSeries(stats: VettingStats | undefined, win: DateRangeWindow): UserSeries[] {
  if (!stats) return [];
  const hasTeam = stats.trackedUsers.length > 0;
  const byUser = new Map<string, UserSeries>();

  const ensure = (id: string, name: string, tracked: boolean, color: string): UserSeries => {
    let series = byUser.get(id);
    if (!series) {
      series = {
        userId: id,
        userName: name,
        tracked,
        color,
        caseTotal: 0,
        casePerBucket: win.bucketLabels.map(() => 0),
        providerTotal: 0,
        providerPerBucket: win.bucketLabels.map(() => 0),
        total: 0,
        lastAt: null,
      };
      byUser.set(id, series);
    }
    return series;
  };

  stats.trackedUsers.forEach((u, i) => ensure(u.id, u.name || u.email, true, USER_COLORS[i % USER_COLORS.length]));

  const applyRows = (rows: CompletionRow[], metric: 'case' | 'provider') => {
    rows.forEach((row) => {
      let series = byUser.get(row.userId);
      if (!series) {
        if (hasTeam) return;
        series = ensure(row.userId, row.userName, false, OTHER_COLOR);
      }
      const idx = win.bucketIndexOf(row.day);
      if (metric === 'case') {
        if (idx >= 0) series.casePerBucket[idx] += row.count;
        series.caseTotal += row.count;
      } else {
        if (idx >= 0) series.providerPerBucket[idx] += row.count;
        series.providerTotal += row.count;
      }
      series.total += row.count;
      if (!series.lastAt || new Date(row.lastAt) > new Date(series.lastAt)) {
        series.lastAt = row.lastAt;
      }
    });
  };

  applyRows(stats.caseCompletions, 'case');
  applyRows(stats.providerCompletions, 'provider');

  const sorted = Array.from(byUser.values()).sort((a, b) => {
    if (a.tracked !== b.tracked) return a.tracked ? -1 : 1;
    return b.total - a.total;
  });

  // Before a team is configured everyone would render as untracked gray;
  // color the top contributors instead — the screen's banner explains the state
  if (!hasTeam) {
    sorted.forEach((s, i) => {
      s.tracked = true;
      s.color = i < USER_COLORS.length ? USER_COLORS[i] : OTHER_COLOR;
    });
  }

  return sorted;
}

export const fmtAge = (iso: string | null) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);
  if (days < 1) return 'today';
  if (days < 60) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'}`;
};
